import { LoadingButton } from '@mui/lab'
import { Alert, Button, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import {
  useAuthLinkPasswordFinishMutation,
  useAuthLinkPasswordStartMutation,
} from 'src/api/mutations/signIn'
import BlockedHint from 'src/components/BlockedHint'
import { useBlockedReason } from 'src/components/useBlockedAction'
import AuthenticatorScan from 'src/pages/AccountSettingsPage/parts/AuthenticatorScan'
import RecoveryCodes from 'src/pages/AccountSettingsPage/parts/RecoveryCodes'
import {
  PASSWORD_MIN_LENGTH,
  newPasswordProblem,
} from 'src/pages/AccountSettingsPage/parts/passwordRules'
import { invitationLinkTokenForget } from 'src/pages/InvitationPage/invitationLinkToken'
import { useLogin } from 'src/pages/LoginPage/parts/useLogin'
import { graphqlErrorCode } from 'src/utils/graphqlErrorCode'
import { resetLinkTokenForget } from './resetLinkToken'

// ----------------------------------------------------------------------
// A password and its authenticator app, set up from an emailed link: an
// invitation, for somebody with no console account yet, or Beast staff's
// reset, for an account staff took the password off.
//
// Three steps, as in Settings: the password, then the app scans a QR code and
// sends one of its codes back, then the recovery codes, shown once. The
// session is started only after the codes have been seen, because signing in
// leaves this page.
// ----------------------------------------------------------------------

type Link = { invitationToken: string } | { resetToken: string }

interface Enrolment {
  enrolmentTicket: string
  secret: string
  qrSvg: string
}

/** Refusals after which the set-up has to start again from the password. */
const START_AGAIN = new Set(['ENROLMENT_EXPIRED'])

const Heading = ({ title, body }: { title: string; body: string }) => (
  <Stack spacing={1}>
    <Typography variant="h2">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {body}
    </Typography>
  </Stack>
)

const attemptsLeftOf = (error: unknown): unknown =>
  (error as { graphQLErrors?: { extensions?: { attemptsLeft?: unknown } }[] })
    ?.graphQLErrors?.[0]?.extensions?.attemptsLeft

export default function LinkPasswordSetup({
  link,
  email,
  onBack,
}: {
  link: Link
  /** The address the account signs in with, as the link names it. */
  email: string
  /** Back to where the person chose a password, when there is such a place. */
  onBack?: () => void
}) {
  const [password, setPassword] = useState('')
  const [repeated, setRepeated] = useState('')
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null)
  const [code, setCode] = useState('')
  const [finished, setFinished] = useState<{
    token: string
    recoveryCodes: string[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [start, { loading: starting }] = useAuthLinkPasswordStartMutation()
  const [finish, { loading: finishing }] = useAuthLinkPasswordFinishMutation()
  const { sessionStart, loading: signingIn } = useLogin()
  const loading = starting || finishing || signingIn

  const passwordProblem = newPasswordProblem(password, repeated)
  const passwordBlocked = useBlockedReason(Boolean(passwordProblem))
  const scanBlocked = useBlockedReason(!code.trim())

  const failed = (failure: unknown) =>
    failure instanceof Error ? failure.message : 'That did not work.'

  const onStart = async (event: React.FormEvent) => {
    event.preventDefault()
    if (passwordProblem) {
      return
    }
    setError(null)
    try {
      const started = (
        await start({ variables: { input: { ...link, password } } })
      ).data?.authLinkPasswordStart
      if (started) {
        setEnrolment(started)
      }
    } catch (failure) {
      setError(failed(failure))
    }
  }

  const onFinish = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!enrolment || !code.trim()) {
      return
    }
    setError(null)
    try {
      const done = (
        await finish({
          variables: {
            input: {
              enrolmentTicket: enrolment.enrolmentTicket,
              code: code.trim(),
            },
          },
        })
      ).data?.authLinkPasswordFinish
      if (done) {
        // The link has done its work either way from here.
        if ('invitationToken' in link) {
          invitationLinkTokenForget()
        } else {
          resetLinkTokenForget()
        }
        setFinished(done)
      }
    } catch (failure) {
      const code = graphqlErrorCode(failure)
      if ((code && START_AGAIN.has(code)) || attemptsLeftOf(failure) === 0) {
        setEnrolment(null)
        setCode('')
      }
      setError(failed(failure))
    }
  }

  if (finished) {
    return (
      <Stack spacing={3}>
        <Heading
          title="Save your recovery codes"
          body="Your password is set. You sign in with it and a code from the app."
        />
        <RecoveryCodes codes={finished.recoveryCodes} />
        <LoadingButton
          variant="contained"
          size="large"
          loading={signingIn}
          onClick={() => void sessionStart(finished.token)}
        >
          Continue to the console
        </LoadingButton>
      </Stack>
    )
  }

  if (enrolment) {
    return (
      <Stack component="form" onSubmit={onFinish} spacing={3}>
        <Heading
          title="Set up your authenticator app"
          body="Scan this with an authenticator app, such as Google Authenticator, Microsoft Authenticator or 1Password, then type the code it shows."
        />
        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}
        <AuthenticatorScan
          qrSvg={enrolment.qrSvg}
          secret={enrolment.secret}
          code={code}
          onCodeChange={setCode}
          disabled={loading}
        />
        <BlockedHint
          reason={scanBlocked.blocked ? 'Enter the code the app shows' : null}
          reasonProps={scanBlocked.reasonProps}
          sx={{ width: '100%' }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            loading={finishing}
            aria-disabled={scanBlocked.blocked}
            disabled={scanBlocked.blocked}
            fullWidth
          >
            Turn on
          </LoadingButton>
        </BlockedHint>
      </Stack>
    )
  }

  return (
    <Stack component="form" onSubmit={onStart} spacing={3}>
      <Heading
        title="Choose a password"
        body={`For ${email}. At least ${String(
          PASSWORD_MIN_LENGTH,
        )} characters: a few words you will remember works well. Next you set up an authenticator app, because a password never signs you in without it.`}
      />
      {error && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        disabled={loading}
        fullWidth
      />
      <TextField
        label="New password again"
        type="password"
        autoComplete="new-password"
        value={repeated}
        onChange={(event) => setRepeated(event.target.value)}
        disabled={loading}
        fullWidth
      />
      <BlockedHint
        reason={passwordBlocked.blocked ? passwordProblem : null}
        reasonProps={passwordBlocked.reasonProps}
        sx={{ width: '100%' }}
      >
        <LoadingButton
          type="submit"
          variant="contained"
          size="large"
          loading={starting}
          aria-disabled={passwordBlocked.blocked}
          disabled={passwordBlocked.blocked}
          fullWidth
        >
          Next
        </LoadingButton>
      </BlockedHint>
      {onBack && (
        <Button variant="text" onClick={onBack} disabled={loading}>
          Back
        </Button>
      )}
    </Stack>
  )
}
